import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/ThemeProvider";

import { SiteSettingsProvider } from "@/contexts/SiteSettingsContext";
import GuardedPage from "@/components/GuardedPage";
import PreviewBanner from "@/components/PreviewBanner";
import SiteBanner from "@/components/SiteBanner";
import RedirectHandler from "@/components/RedirectHandler";
import DynamicSlugResolver from "@/components/DynamicSlugResolver";
import ScrollToTop from "./components/ScrollToTop";
import BreakingNewsToast from "./components/BreakingNewsToast";

// ── Eagerly loaded: homepage (critical path) ──
import Index from "./pages/Index";

// ── Lazy-loaded pages ──
const Summit = lazy(() => import("./pages/Summit"));
const News = lazy(() => import("./pages/News"));
const ArticleDetail = lazy(() => import("./pages/ArticleDetail"));
const Learn = lazy(() => import("./pages/Learn"));
const GuideDetail = lazy(() => import("./pages/GuideDetail"));
const SearchPage = lazy(() => import("./pages/Search"));
const About = lazy(() => import("./pages/About"));
const EditorialTeam = lazy(() => import("./pages/EditorialTeam"));
const EditorialGuidelines = lazy(() => import("./pages/EditorialGuidelines"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const Contact = lazy(() => import("./pages/Contact"));
const Scorecard = lazy(() => import("./pages/Scorecard"));
const Markets = lazy(() => import("./pages/Markets"));
const PredictionMarketsHub = lazy(() => import("./pages/PredictionMarketsHub"));
const LegalHub = lazy(() => import("./pages/LegalHub"));
const AppsHub = lazy(() => import("./pages/AppsHub"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PressKit = lazy(() => import("./pages/PressKit"));
const Announcements = lazy(() => import("./pages/Announcements"));
const AnnouncementDetail = lazy(() => import("./pages/AnnouncementDetail"));
const JournalistProfile = lazy(() => import("./pages/JournalistProfile"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const CustomPageView = lazy(() => import("./pages/CustomPageView"));

const queryClient = new QueryClient();

const g = (path: string, element: React.ReactNode) => (
  <GuardedPage path={path}>{element}</GuardedPage>
);

/** Lightweight skeleton shown while a lazy chunk loads */
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      <span className="text-xs text-muted-foreground font-medium tracking-wide">Loading</span>
    </div>
  </div>
);

const App = () => (
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <SiteSettingsProvider>
      
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <RedirectHandler />
          <SiteBanner />
          <PreviewBanner />
          <BreakingNewsToast />
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/summit" element={g("/summit", <Summit />)} />
            <Route path="/news" element={g("/news", <News />)} />
            <Route path="/news/:category/:slug" element={<ArticleDetail />} />
            <Route path="/learn" element={g("/learn", <Learn />)} />
            <Route path="/learn/:slug" element={<GuideDetail />} />
            <Route path="/search" element={g("/search", <SearchPage />)} />
            <Route path="/about" element={g("/about", <About />)} />
            <Route path="/about/editorial-team" element={g("/about/editorial-team", <EditorialTeam />)} />
            <Route path="/about/editorial-team/:slug" element={<JournalistProfile />} />
            <Route path="/about/editorial-guidelines" element={g("/about/editorial-guidelines", <EditorialGuidelines />)} />
            <Route path="/about/privacy-policy" element={g("/about/privacy-policy", <PrivacyPolicy />)} />
            <Route path="/about/terms-and-conditions" element={g("/about/terms-and-conditions", <TermsAndConditions />)} />
            <Route path="/about/press" element={g("/about/press", <PressKit />)} />
            <Route path="/about/announcements" element={g("/about/announcements", <Announcements />)} />
            <Route path="/about/announcements/:slug" element={<AnnouncementDetail />} />
            <Route path="/contact" element={g("/contact", <Contact />)} />
            <Route path="/scorecard" element={g("/scorecard", <Scorecard />)} />
            <Route path="/markets" element={g("/markets", <Markets />)} />
            <Route path="/prediction-markets" element={g("/prediction-markets", <PredictionMarketsHub />)} />
            <Route path="/prediction-market-apps" element={g("/prediction-market-apps", <AppsHub />)} />
            <Route path="/legal" element={g("/legal", <LegalHub />)} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/p/:slug" element={<CustomPageView />} />
            <Route path="/:slug" element={<DynamicSlugResolver />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
      
      </SiteSettingsProvider>
    </ThemeProvider>
  </QueryClientProvider>
  </HelmetProvider>
);

export default App;
